import { ITreeItem } from "@/interfaces/ITreeItem";
import { PtTreeItem } from "./components/PtTreeItem";
import { SearchService } from "@/services";
import { FilterListMessage, Loading, SearchInput } from "@/components";
import { IPanelScope, ISearchResult } from "@/interfaces";
import { useEffect, useRef, useState, useMemo } from "react";
import { DEFAULT_FILTER_KEY, MIN_CRITERIA_LENGTH } from "@/constants";
import { findNodeById } from "@/utils/common";
import styles from "./SearchableTree.module.scss";
import {
  SEARCHABLE_TREE_SUGGESTIONS_TESTID,
  SEARCHABLE_TREE_CONTAINER_TESTID,
  SEARCHABLE_TREE_NO_ITEMS_FOUND_TESTID,
} from "@/constants/test-ids";

interface SearchableTreeProps {
  scope: IPanelScope;
  items: ITreeItem[];
  isLoading: boolean;
  error: string | undefined;
  selectedItem: ITreeItem | undefined;
  handleNodeExpand: (node: ITreeItem) => void;
  handleSelected: (node: ITreeItem) => void;
  handleSearch?: (criteria: string) => Promise<ISearchResult | undefined>;
  width?: number | string;
  addSelectAll?: boolean;
  customClassName?: string;
}

export const SearchableTree = ({
  scope,
  items,
  selectedItem,
  width,
  error,
  isLoading,
  addSelectAll = true,
  handleSelected,
  handleNodeExpand,
  handleSearch,
}: SearchableTreeProps) => {
  const [criteria, setCriteria] = useState<string>("");
  const [openNodes, setOpenNodes] = useState<Set<string>>(new Set());
  const openNodesArray = useMemo(() => Array.from(openNodes), [openNodes]);
  const [visibleItems, setVisibleItems] = useState<ITreeItem[]>([]);
  const refFirstTimeOpen = useRef(true);

  useEffect(() => {
    (async () => {
      if (criteria.length > MIN_CRITERIA_LENGTH) {
        const searchResult = handleSearch
          ? await handleSearch(criteria)
          : SearchService.search(items, criteria);

        if (!searchResult) {
          return;
        }

        const { filteredTree } = searchResult;

        const _openNodes: string[] = items?.length
          ? SearchService.getSearchOpenNodes(filteredTree, criteria)
          : [];

        setVisibleItems(filteredTree);
        setOpenNodes(new Set(_openNodes));
      } else {
        setVisibleItems(items);
        setOpenNodes(new Set([]));
      }
    })();
  }, [criteria, items]);

  useEffect(() => {
    if (isLoading) return;

    if (selectedItem?.id && refFirstTimeOpen.current) {
      refFirstTimeOpen.current = false;
      const _openNodes = selectedItem?.serverObject?.ancestry?.split("/") || [];

      // in case of auto field, we need to open the parent node
      const idParts = selectedItem?.id.split("/");
      if (idParts.length > 2) {
        _openNodes.push(idParts.slice(0, 3).join("/"));
      }

      setOpenNodes(new Set<string>(_openNodes || []));
      for (const node of _openNodes) {
        const parentItemTree = findNodeById(items, node);
        if (parentItemTree) {
          handleNodeExpand(parentItemTree);
        }
      }
    }
  }, [isLoading]);

  const handleToggleExpandState = async (node: ITreeItem) => {
    if (!openNodes.has(node.id)) {
      await handleNodeExpand(node);
    }

    setOpenNodes((prev) => {
      const newOpenNodes = new Set(prev);
      if (newOpenNodes.has(node.id)) {
        newOpenNodes.delete(node.id);
      } else {
        newOpenNodes.add(node.id);
      }
      return newOpenNodes;
    });
  };

  const handleCriteriaChange = (value: string) => {
    setCriteria(value);
  };

  return width ? (
    <div
      className={`${styles.searchContainer} suggestions-container`}
      style={{ width }}
      data-testid={SEARCHABLE_TREE_SUGGESTIONS_TESTID}
    >
      <SearchInput
        scope={scope}
        isActive={true}
        criteria={criteria}
        handleCriteriaChange={handleCriteriaChange}
        customClassName={styles.customSeatchClassName}
      />
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <Loading size="modal" />
        </div>
      ) : error ? (
        <FilterListMessage message={error} />
      ) : (
        <ul
          className={styles.container}
          data-testid={SEARCHABLE_TREE_CONTAINER_TESTID}
        >
          {!visibleItems.length && criteria.length > MIN_CRITERIA_LENGTH ? (
            <FilterListMessage
              dataTestId={SEARCHABLE_TREE_NO_ITEMS_FOUND_TESTID}
              message="No items found"
            />
          ) : (
            addSelectAll && (
              <PtTreeItem
                data={{
                  id: DEFAULT_FILTER_KEY,
                  name: `All ${scope.module}`,
                  key: `all_${scope.module}`,
                  children: [],
                  extra: {
                    hasChildren: false,
                    paddingLeft: 0,
                  },
                }}
                selectedItem={selectedItem}
                level="all"
                isRoot={true}
                key={`all_${scope.module}`}
                handleSelect={handleSelected}
                openNodes={openNodesArray}
                handleToggleExpandState={handleToggleExpandState}
              />
            )
          )}
          {visibleItems?.map((item, index) => (
            <PtTreeItem
              isLoading={item.extra.isLoading}
              selectedItem={selectedItem}
              data={item}
              level={`${index}`}
              error={item.extra.error}
              isRoot={true}
              key={item.key}
              handleSelect={handleSelected}
              openNodes={openNodesArray}
              handleToggleExpandState={handleToggleExpandState}
            />
          ))}
        </ul>
      )}
    </div>
  ) : null;
};
